import { useCallback } from 'react';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { AIArtifactOptions } from 'models/AIArtifact';
import { AIJobStatus, AIJobType } from 'models/AIJob';
import { AI_JOB_CHANNEL } from 'services/api/constants';

export type AIJobChannelMessage = {
  id: string;
  status: AIJobStatus;
  type: AIJobType;
  error?: string;
  config?: {
    attachmentIds?: string[];
  };
  aiJobbableId: string;
  aiArtifact?: AIArtifactOptions;
};

// ActionCable subscribe params bypass OliveBranch (which only runs as Rack
// middleware for HTTP), so keys must be snake_case to match what the Rails
// channel reads (see api/app/channels/ai_job_channel.rb and ai_job_channel_spec.rb).
//
// `attachable_model` is only used server-side when the ai_jobbable is an
// Attachment — it scopes which Attachment rows the user can see. For jobs
// attached directly to a Review (admissions, referrals, auths) or a
// Summarization, the policy ignores it entirely, so callers can omit it.
// (If we send an invalid value, the server raises and SubscriptionGuarantor
// retries every 500ms forever.)
type AIJobChannelParams = {
  ai_job_id?: string;
  attachable_model?: string;
};

type UseAIJobChannelOptions = {
  params: AIJobChannelParams;
  onMessageReceived?: (data: AIJobChannelMessage) => void;
};

export default function useAIJobChannel({ params, onMessageReceived }: UseAIJobChannelOptions) {
  const handleReceived = useCallback(
    (data: AIJobChannelMessage) => {
      onMessageReceived?.(data);
    },
    [onMessageReceived]
  );

  const { isConnected } = useWebsocketSubscription<AIJobChannelMessage>({
    channel: AI_JOB_CHANNEL,
    params,
    onReceived: handleReceived,
    enabled: !!params.ai_job_id,
  });

  return {
    isSubscribed: isConnected,
  };
}
